import { useTranslation } from 'react-i18next';

import { ApiError, ConflictError, ForbiddenError } from '@/api/errors';
import { Alert } from '@/app/components/ui/Alert';

/**
 * Renders a failed write as a clear sentence. The two cases the role model
 * makes likely get their own wording: a 403 (the server's authorization is
 * the real boundary, whatever the UI showed) and a 409 (the write collided
 * with current state).
 */
export function SubmitError({ error }: { error: Error | null }) {
  const { t } = useTranslation();
  if (!error) return null;

  // `reason` comes back from the API in English and is not translated; only
  // the sentence around it is. `suffix` is built here rather than in the
  // catalogue, since it's punctuation logic, not text.
  const suffix = (reason: string | undefined) => (reason ? `: ${reason}` : '.');

  let message: string;
  if (error instanceof ForbiddenError) {
    message = t('errors.submitForbidden', { suffix: suffix(error.reason) });
  } else if (error instanceof ConflictError) {
    message = t('errors.submitConflict', { suffix: suffix(error.reason) });
  } else if (error instanceof ApiError) {
    message = error.message;
  } else {
    message = t('errors.submitGeneric', { message: error.message });
  }

  return <Alert tone="danger">{message}</Alert>;
}
